import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  CheckCircle as CompleteIcon,
  Assignment as WorkOrderIcon,
  Build as ProductionIcon,
  Timeline as TimelineIcon,
  Inventory as MaterialIcon
} from '@mui/icons-material';

const ProductionManagement = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [workstations, setWorkstations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    productId: '',
    quantity: 1,
    priority: 'normal',
    startDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: '',
    materials: [],
    workstations: []
  });

  const [currentMaterial, setCurrentMaterial] = useState({
    materialId: '',
    quantity: 1,
    unitCost: 0
  });

  const [currentWorkstation, setCurrentWorkstation] = useState({
    workstationId: '',
    estimatedHours: 1,
    actualHours: 0
  });

  useEffect(() => {
    fetchWorkOrders();
    fetchProducts();
    fetchMaterials();
    fetchWorkstations();
  }, []);

  const fetchWorkOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/production/work-orders');
      if (response.ok) {
        const data = await response.json();
        setWorkOrders(data);
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
      showSnackbar('İş emirleri yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products');
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await fetch('/api/materials');
      if (response.ok) {
        const data = await response.json();
        setMaterials(data);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const fetchWorkstations = async () => {
    try {
      const response = await fetch('/api/production/workstations');
      if (response.ok) {
        const data = await response.json();
        setWorkstations(data);
      }
    } catch (error) {
      console.error('Error fetching workstations:', error);
    }
  };

  const handleOpenDialog = (workOrder = null) => {
    if (workOrder) {
      setEditingWorkOrder(workOrder);
      setFormData({
        productId: workOrder.productId,
        quantity: workOrder.quantity,
        priority: workOrder.priority,
        startDate: workOrder.startDate.split('T')[0],
        dueDate: workOrder.dueDate ? workOrder.dueDate.split('T')[0] : '',
        notes: workOrder.notes,
        materials: workOrder.materials || [],
        workstations: workOrder.workstations || []
      });
    } else {
      setEditingWorkOrder(null);
      setFormData({
        productId: '',
        quantity: 1,
        priority: 'normal',
        startDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        notes: '',
        materials: [],
        workstations: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingWorkOrder(null);
  };

  const handleAddMaterial = () => {
    if (currentMaterial.materialId && currentMaterial.quantity > 0) {
      const material = materials.find(m => m.id === currentMaterial.materialId);
      const newMaterial = {
        ...currentMaterial,
        materialName: material.name,
        totalCost: currentMaterial.quantity * currentMaterial.unitCost
      };
      
      setFormData({
        ...formData,
        materials: [...formData.materials, newMaterial]
      });
      
      setCurrentMaterial({
        materialId: '',
        quantity: 1,
        unitCost: 0
      });
    }
  };

  const handleAddWorkstation = () => {
    if (currentWorkstation.workstationId && currentWorkstation.estimatedHours > 0) {
      const workstation = workstations.find(w => w.id === currentWorkstation.workstationId);
      const newWorkstation = {
        ...currentWorkstation,
        workstationName: workstation.name
      };
      
      setFormData({
        ...formData,
        workstations: [...formData.workstations, newWorkstation]
      });
      
      setCurrentWorkstation({
        workstationId: '',
        estimatedHours: 1,
        actualHours: 0
      });
    }
  };

  const handleRemoveMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleRemoveWorkstation = (index) => {
    const newWorkstations = formData.workstations.filter((_, i) => i !== index);
    setFormData({ ...formData, workstations: newWorkstations });
  };

  const handleSubmit = async () => {
    if (!formData.productId || formData.quantity <= 0) {
      showSnackbar('Ürün ve miktar seçilmelidir', 'error');
      return;
    }

    try {
      const url = editingWorkOrder 
        ? `/api/production/work-orders/${editingWorkOrder.id}`
        : '/api/production/work-orders';
      
      const method = editingWorkOrder ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showSnackbar(
          editingWorkOrder ? 'İş emri güncellendi' : 'İş emri oluşturuldu',
          'success'
        );
        handleCloseDialog();
        fetchWorkOrders();
      } else {
        const error = await response.json();
        showSnackbar(error.message || 'Bir hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Error saving work order:', error);
      showSnackbar('İş emri kaydedilirken hata oluştu', 'error');
    }
  };

  const handleStatusChange = async (workOrderId, newStatus) => {
    try {
      const response = await fetch(`/api/production/work-orders/${workOrderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        showSnackbar('İş emri durumu güncellendi', 'success');
        fetchWorkOrders();
      } else {
        showSnackbar('Durum güncellenirken hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Error updating work order status:', error);
      showSnackbar('Durum güncellenirken hata oluştu', 'error');
    }
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    if (!window.confirm('Bu iş emrini silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(`/api/production/work-orders/${workOrderId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showSnackbar('İş emri silindi', 'success');
        fetchWorkOrders();
      } else {
        showSnackbar('İş emri silinirken hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Error deleting work order:', error);
      showSnackbar('İş emri silinirken hata oluştu', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'planned': 'Planlandı',
      'in_progress': 'Üretimde',
      'completed': 'Tamamlandı',
      'cancelled': 'İptal Edildi'
    };
    return statusMap[status] || status;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      'high': 'Yüksek',
      'normal': 'Normal',
      'low': 'Düşük'
    };
    return priorityMap[priority] || priority;
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Bilinmiyor';
  };

  const filteredWorkOrders = workOrders.filter(workOrder => {
    const matchesStatus = filterStatus === 'all' || workOrder.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      getProductName(workOrder.productId).toLowerCase().includes(searchTerm.toLowerCase()) ||
      workOrder.workOrderNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalMaterialCost = formData.materials.reduce((sum, material) => sum + material.totalCost, 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Üretim Yönetimi
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Yeni İş Emri
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="İş Emri Ara"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkOrderIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Durum Filtresi</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Durum Filtresi"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="planned">Planlandı</MenuItem>
                  <MenuItem value="in_progress">Üretimde</MenuItem>
                  <MenuItem value="completed">Tamamlandı</MenuItem>
                  <MenuItem value="cancelled">İptal Edildi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>İş Emri No</TableCell>
                  <TableCell>Ürün</TableCell>
                  <TableCell>Miktar</TableCell>
                  <TableCell>Öncelik</TableCell>
                  <TableCell>Başlangıç</TableCell>
                  <TableCell>Bitiş</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWorkOrders.map((workOrder) => (
                  <TableRow key={workOrder.id}>
                    <TableCell>{workOrder.workOrderNumber}</TableCell>
                    <TableCell>{getProductName(workOrder.productId)}</TableCell>
                    <TableCell>{workOrder.quantity}</TableCell>
                    <TableCell>
                      <Chip
                        label={getPriorityText(workOrder.priority)}
                        color={getPriorityColor(workOrder.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(workOrder.startDate).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell>
                      {workOrder.dueDate ? new Date(workOrder.dueDate).toLocaleDateString('tr-TR') : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(workOrder.status)}
                        color={getStatusColor(workOrder.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(workOrder)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      {workOrder.status === 'planned' && (
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(workOrder.id, 'in_progress')}
                          color="warning"
                        >
                          <StartIcon />
                        </IconButton>
                      )}
                      {workOrder.status === 'in_progress' && (
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(workOrder.id, 'completed')}
                          color="success"
                        >
                          <CompleteIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteWorkOrder(workOrder.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Work Order Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingWorkOrder ? 'İş Emri Düzenle' : 'Yeni İş Emri Oluştur'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Ürün</InputLabel>
                <Select
                  value={formData.productId}
                  onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                  label="Ürün"
                  required
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Miktar"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Öncelik</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Öncelik"
                >
                  <MenuItem value="low">Düşük</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">Yüksek</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Başlangıç Tarihi"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bitiş Tarihi"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notlar"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>

            {/* Materials Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Malzemeler
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Malzeme</InputLabel>
                    <Select
                      value={currentMaterial.materialId}
                      onChange={(e) => {
                        const material = materials.find(m => m.id === e.target.value);
                        setCurrentMaterial({
                          ...currentMaterial,
                          materialId: e.target.value,
                          unitCost: material?.cost || 0
                        });
                      }}
                      label="Malzeme"
                    >
                      {materials.map((material) => (
                        <MenuItem key={material.id} value={material.id}>
                          {material.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Miktar"
                    type="number"
                    value={currentMaterial.quantity}
                    onChange={(e) => setCurrentMaterial({ ...currentMaterial, quantity: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Birim Maliyet"
                    type="number"
                    value={currentMaterial.unitCost}
                    onChange={(e) => setCurrentMaterial({ ...currentMaterial, unitCost: parseFloat(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleAddMaterial}
                    sx={{ height: 56 }}
                  >
                    Ekle
                  </Button>
                </Grid>
              </Grid>

              <List>
                {formData.materials.map((material, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={material.materialName}
                      secondary={`${material.quantity} adet x ${material.unitCost} TL`}
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="h6">
                        {material.totalCost.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                      </Typography>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveMaterial(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Workstations Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                İş İstasyonları
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>İş İstasyonu</InputLabel>
                    <Select
                      value={currentWorkstation.workstationId}
                      onChange={(e) => setCurrentWorkstation({ ...currentWorkstation, workstationId: e.target.value })}
                      label="İş İstasyonu"
                    >
                      {workstations.map((workstation) => (
                        <MenuItem key={workstation.id} value={workstation.id}>
                          {workstation.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Tahmini Saat"
                    type="number"
                    value={currentWorkstation.estimatedHours}
                    onChange={(e) => setCurrentWorkstation({ ...currentWorkstation, estimatedHours: parseFloat(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Gerçekleşen Saat"
                    type="number"
                    value={currentWorkstation.actualHours}
                    onChange={(e) => setCurrentWorkstation({ ...currentWorkstation, actualHours: parseFloat(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleAddWorkstation}
                    sx={{ height: 56 }}
                  >
                    Ekle
                  </Button>
                </Grid>
              </Grid>

              <List>
                {formData.workstations.map((workstation, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={workstation.workstationName}
                      secondary={`Tahmini: ${workstation.estimatedHours}h, Gerçekleşen: ${workstation.actualHours}h`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveWorkstation(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {formData.materials.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="h6">
                    Toplam Malzeme Maliyeti: {totalMaterialCost.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingWorkOrder ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductionManagement; 